/**
 * THE ORG LOYALTY PORTAL — the clinic's front-desk card page.
 *
 * Client / Staff / Management, in the clinic's own colours, fonts, wallpaper
 * and wording, because it reads the SAME loyalty design as the member portal.
 * A member signs in here so she never hands her phone over.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { BackBar, BigButton, Field, Light, Screen, Title } from '../../components/loyalty/ui';
import { handOverSession, heldSession, rememberMe } from './session';
import { QrScan, canScan } from '../../components/loyalty/QrScan';
import { money, moneyFieldProps, plain, amount as amountOnly } from '../../lib/money';

const API = typeof window !== 'undefined' ? window.location.origin : '';

export default function LoyaltyDesk() {
    const { cardId } = useParams();
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const [cfg, setCfg] = useState(null);
    const [gone, setGone] = useState('');
    const [screen, setScreen] = useState('home');
    const [form, setForm] = useState({});
    const [staff, setStaff] = useState(null);
    const [checkins, setCheckins] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState('');
    const [scanning, setScanning] = useState(false);
    const [desk, setDesk] = useState(null);

    const when = (iso) => {
        try {
            return new Date(iso).toLocaleTimeString(lang === 'fr' ? 'fr-CA'
                : lang === 'es' ? 'es-ES' : 'en-CA', { hour: 'numeric', minute: '2-digit' });
        } catch { return ''; }
    };

    useEffect(() => {
        fetch(`${API}/api/loyalty-desk/${cardId}?lang=${lang}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(setCfg)
            .catch((code) => setGone(code === 403 ? 'off' : 'unknown'));
    }, [cardId, lang]);

    const theme = { ...(cfg?.design?.theme || {}),
        lux_buttons: !!cfg?.design?.branding?.lux_buttons };
    const slots = cfg?.slots || [];
    const slot = (id) => slots.find((s) => s.id === id) || {};
    const shown = (id) => slot(id).visible !== false;
    const text = (id, fallback) => slot(id).label?.[lang] || slot(id).label?.en || fallback;
    const paint = (id) => ({ bg: slot(id).bg || '', fg: slot(id).fg || '',
        lux: !!cfg?.design?.branding?.lux_buttons });
    // The lights are a clinic's choice, and only a signed-in manager ever sees them.
    const lightsOn = cfg?.design?.branding?.desk_lights !== false && !!staff;

    const post = useCallback(async (path, body, headers = {}) => {
        setBusy(true);
        setError('');
        try {
            const r = await fetch(`${API}/api/loyalty-desk/${cardId}${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...headers },
                body: JSON.stringify(body || {}),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                const reason = typeof data.detail === 'object' ? data.detail.reason : data.detail;
                // A session left alone too long (or switched off) sends the desk
                // back to the team sign in, with nothing of the last person left.
                if (r.status === 401) {
                    setStaff(null);
                    setCheckins([]);
                    setForm({});
                    setScreen('staff_signin');
                }
                setError(String(reason || 'failed'));
                return null;
            }
            if (data.session) setStaff((was) => (was ? { ...was, token: data.session } : was));
            return data;
        } finally { setBusy(false); }
    }, [cardId]);

    const loadCheckins = useCallback(async (token) => {
        const r = await fetch(`${API}/api/loyalty-desk/${cardId}/checkins`,
            { headers: { 'X-Loyalty-Staff': token } });
        if (r.status === 401) {
            setStaff(null);
            setCheckins([]);
            setScreen('staff_signin');
            setError('bad_session');
            return;
        }
        const data = await r.json().catch(() => ({}));
        setCheckins(data.checkins || []);
        if (data.session) setStaff((was) => (was ? { ...was, token: data.session } : was));
    }, [cardId]);

    const loadDesk = useCallback(async (token) => {
        const r = await fetch(`${API}/api/loyalty-desk/${cardId}/manager/pending`,
            { headers: { 'X-Loyalty-Staff': token } });
        if (!r.ok) {
            if (r.status === 401) { setStaff(null); setScreen('staff_signin'); }
            return;
        }
        const data = await r.json().catch(() => ({}));
        setDesk(data);
        if (data.session) setStaff((was) => (was ? { ...was, token: data.session } : was));
    }, [cardId]);

    // THE LIGHTS, KEPT FRESH. A gentle look every 30 seconds while a manager is
    // on the desk. It deliberately does NOT take the fresh session the server
    // hands back, so waiting on this screen does not hold the 30-minute idle
    // sign-out open.
    useEffect(() => {
        if (!staff || !['manager', 'pending', 'staff', 'awards'].includes(screen)) return undefined;
        const token = staff.token;
        const look = async () => {
            const heads = { headers: { 'X-Loyalty-Staff': token } };
            const [one, two] = await Promise.all([
                fetch(`${API}/api/loyalty-desk/${cardId}/checkins`, heads),
                staff.role === 'manager'
                    ? fetch(`${API}/api/loyalty-desk/${cardId}/manager/pending`, heads)
                    : Promise.resolve(null),
            ]);
            if (one?.status === 401 || two?.status === 401) {
                // The 30-minute idle sign-out has happened: back to sign in,
                // rather than leaving stale lights on the screen.
                setStaff(null);
                setDesk(null);
                setCheckins([]);
                setScreen('staff_signin');
                setError('bad_session');
                return;
            }
            if (one?.ok) setCheckins((await one.json().catch(() => ({}))).checkins || []);
            if (two?.ok) setDesk(await two.json().catch(() => null));
        };
        const timer = window.setInterval(look, 30000);
        return () => window.clearInterval(timer);
    }, [staff, screen, cardId]);


    if (gone) {
        return (
            <Screen theme={{}} testid="loyalty-desk-off">
                <p className="p-6 text-center text-sm" data-testid="loyalty-desk-off-line">
                    {t('loyalty.desk.off', 'This card is not in use.')}
                </p>
            </Screen>
        );
    }
    if (!cfg) {
        return <Screen theme={{}} testid="loyalty-desk-loading">
            <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin" />
        </Screen>;
    }

    const clientIn = async () => {
        const held = heldSession();
        const out = await post('/client',
            { email: form.email, pin: form.pin },
            held ? { 'X-Loyalty-Session': held } : {});
        if (out) {
            handOverSession(out.session);
            rememberMe({ email: form.email || '', first_name: out.first_name || '' });
            setDone(t('loyalty.desk.checkedIn', 'Thank you {{name}} — the desk has you.',
                { name: out.first_name || '' }));
            setForm({});
            setScreen('checked_in');
        }
    };

    const staffIn = async () => {
        const out = await post('/staff/signin', { email: form.email, pin: form.pin });
        if (out) {
            setStaff({ ...out.staff, token: out.session, permissions: out.permissions });
            setForm({});
            const wantsManager = out.staff.role === 'manager' && form.wanted === 'manager';
            // The manager's first screen IS the waiting list — no menu in between.
            setScreen(wantsManager ? 'pending' : 'staff');
            if (wantsManager) await loadDesk(out.session);
            await loadCheckins(out.session);
        }
    };

    const decide = async (decision, line = null, override = true) => {
        const row = line || form.award;
        const typed = override ? plain(form.amount) : '';
        const changed = typed !== '' && Number(typed) !== Number(row.bill_amount);
        const out = await post('/manager/decide', {
            award_id: row.id,
            decision,
            amount: changed ? Number(typed) : null,
            reason: form.reason || '',
        }, { 'X-Loyalty-Staff': staff.token });
        if (out) {
            setDone(decision === 'approve'
                ? t('loyalty.desk.approvedOne', '{{points}} points added to {{name}}',
                    { points: Number(out.points).toLocaleString(), name: row.member_name })
                : t('loyalty.desk.refusedOne', 'No points for {{name}}',
                    { name: row.member_name }));
            setForm({});
            await loadDesk(out.session || staff.token);
            setScreen('pending');
        }
    };

    const award = async () => {
        const out = await post('/award',
            { checkin_id: form.checkin_id, amount: Number(plain(form.amount)) },
            { 'X-Loyalty-Staff': staff.token });
        if (out) {
            setDone(out.pending
                ? t('loyalty.desk.awardedWaiting',
                    '{{points}} points for {{name}} are waiting for approval',
                    { points: Number(out.points).toLocaleString(), name: out.first_name })
                : t('loyalty.desk.awardedOne', '{{points}} points added to {{name}}',
                    { points: Number(out.points).toLocaleString(), name: out.first_name }));
            setForm({});
            setScreen('added');
            await loadCheckins(out.session || staff.token);
            // Back to the list by itself, so the desk is ready for the next person.
            window.setTimeout(() => {
                setDone('');
                setScreen((now) => (now === 'added' ? 'staff' : now));
            }, 3000);
        }
    };

    const lookUp = async (code) => {
        if (!String(code || '').trim()) return;
        const out = await post('/redeem/look-up', { code },
            { 'X-Loyalty-Staff': staff.token });
        if (out) setForm((f) => ({ ...f, kind: out.certificate ? 'certificate' : 'card' }));
    };

    const redeem = async () => {
        const out = await post('/redeem',
            { code: form.code, bill: Number(plain(form.bill)) || null,
              checkin_id: form.checkin_id || '' },
            { 'X-Loyalty-Staff': staff.token });
        if (out) {
            const extra = (out.giver_points || out.friend_points)
                ? ' ' + t('loyalty.desk.referralGiven',
                    '{{giver}} points to the giver, {{friend}} to her friend.',
                    { giver: out.giver_points || 0, friend: out.friend_points || 0 })
                : '';
            setDone(t('loyalty.desk.redeemed', 'Gift card {{code}} used — {{value}}',
                { code: out.code, value: money(out.value, lang) }) + extra);
            setForm({});
            setScreen('staff');
            await loadCheckins(staff.token);
        }
    };

    const errorLine = error ? (
        <p className="text-center text-sm text-rose-300" data-testid="loyalty-desk-error">
            {String(error).startsWith('below_minimum')
                ? t('loyalty.desk.error.below_minimum',
                    'Bill is below the minimum of {{amount}} for this certificate',
                    { amount: money(Number(String(error).split(':')[1] || 0), lang) })
                : t(`loyalty.desk.error.${screen === 'redeem' && error === 'expired'
                    ? 'card_expired' : error}`, t(`loyalty.error.${error}`, error))}
        </p>
    ) : null;

    return (
        <Screen theme={theme} branding={cfg.design?.branding} testid="loyalty-desk">
            <div className="mx-auto w-full max-w-md space-y-4 p-5">
                {screen === 'home' && (
                    <>
                        <Title theme={theme} testid="loyalty-desk-title">
                            {text('desk_title', cfg.program_name
                                || t('loyalty.desk.title', 'Welcome'))}
                        </Title>
                        <BigButton theme={theme} {...paint('desk_client_btn')}
                                   testid="loyalty-desk-client"
                                   onClick={() => { setScreen('client'); setDone(''); }}>
                            {text('desk_client_btn', t('loyalty.desk.client', 'Client'))}
                        </BigButton>
                        <BigButton theme={theme} {...paint('desk_staff_btn')} tone="quiet"
                                   testid="loyalty-desk-staff"
                                   onClick={() => { setForm({ wanted: 'staff' }); setScreen('staff_signin'); }}>
                            {text('desk_staff_btn', t('loyalty.desk.staff', 'Staff'))}
                        </BigButton>
                        <BigButton theme={theme} {...paint('desk_manager_btn')} tone="quiet"
                                   testid="loyalty-desk-manager"
                                   onClick={() => { setForm({ wanted: 'manager' }); setScreen('staff_signin'); }}>
                            {text('desk_manager_btn', t('loyalty.desk.manager', 'Management'))}
                        </BigButton>
                    </>
                )}

                {screen === 'client' && (
                    <>
                        <BackBar onBack={() => setScreen('home')} label={t('common.back', 'Back')} />
                        <Title theme={theme}>{t('loyalty.desk.clientTitle', 'Sign in')}</Title>
                        <Field theme={theme} label={t('loyalty.email', 'Email')} value={form.email || ''}
                               testid="loyalty-desk-client-email"
                               onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <Field theme={theme} label={t('loyalty.pin', 'PIN')} value={form.pin || ''} type="password"
                               inputMode="numeric" testid="loyalty-desk-client-pin"
                               onChange={(e) => setForm({ ...form, pin: e.target.value })} />
                        {errorLine}
                        <BigButton theme={theme} disabled={busy} testid="loyalty-desk-client-go"
                                   onClick={clientIn}>
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                                : t('loyalty.signIn', 'Sign in')}
                        </BigButton>
                    </>
                )}

                {screen === 'checked_in' && (
                    <>
                        <Title theme={theme} testid="loyalty-desk-checkedin-title">
                            {done}
                        </Title>
                        {shown('desk_lounge_btn') && (
                            <BigButton theme={theme} {...paint('desk_lounge_btn')}
                                       testid="loyalty-desk-lounge"
                                       onClick={() => { window.location.href = `/loyalty/${cardId}?lounge=1`; }}>
                                {text('desk_lounge_btn', t('loyalty.desk.lounge', 'My VIP Lounge'))}
                            </BigButton>
                        )}
                        <BigButton theme={theme} tone="quiet" testid="loyalty-desk-checkedin-done"
                                   onClick={() => { setDone(''); setScreen('home'); }}>
                            {t('common.close', 'Close')}
                        </BigButton>
                    </>
                )}

                {screen === 'added' && (
                    <>
                        <Title theme={theme} testid="loyalty-desk-added-title">{done}</Title>
                        <BigButton theme={theme} tone="quiet" testid="loyalty-desk-added-next"
                                   onClick={() => { setDone(''); setScreen('staff'); }}>
                            {t('loyalty.desk.nextPerson', 'Next person')}
                        </BigButton>
                    </>
                )}

                {screen === 'staff_signin' && (
                    <>
                        <BackBar onBack={() => setScreen('home')} label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-desk-signin-title">
                            {text('desk_signin_title', t('loyalty.desk.signInTitle', 'Team sign in'))}
                        </Title>
                        <Field theme={theme} label={t('loyalty.email', 'Email')} value={form.email || ''}
                               testid="loyalty-desk-staff-email"
                               onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <Field theme={theme} label={t('loyalty.pin', 'PIN')} value={form.pin || ''} type="password"
                               inputMode="numeric" testid="loyalty-desk-staff-pin"
                               onChange={(e) => setForm({ ...form, pin: e.target.value })} />
                        {errorLine}
                        <BigButton theme={theme} {...paint('desk_signin_btn')} disabled={busy}
                                   testid="loyalty-desk-staff-go" onClick={staffIn}>
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                                : text('desk_signin_btn', t('loyalty.desk.signIn', 'Sign in'))}
                        </BigButton>
                    </>
                )}

                {screen === 'staff' && staff && (
                    <>
                        <BackBar onBack={() => { setStaff(null); setScreen('home'); }}
                                 label={t('loyalty.desk.signOut', 'Sign out')} />
                        <Title theme={theme} testid="loyalty-desk-checkins-title">
                            {text('desk_checkins_title',
                                t('loyalty.desk.checkinsTitle', "Today's check-ins"))}
                        </Title>
                        {done && <p className="text-center text-sm" data-testid="loyalty-desk-done">{done}</p>}
                        {errorLine}
                        {checkins.length === 0 && (
                            <p className="text-center text-sm opacity-80" data-testid="loyalty-desk-empty">
                                {t('loyalty.desk.noCheckins', 'Nobody has signed in yet.')}
                            </p>
                        )}
                        {checkins.map((c) => (
                            <BigButton key={c.id} theme={theme} tone="quiet"
                                       testid={`loyalty-desk-checkin-${c.id}`}
                                       onClick={() => { setForm({ checkin_id: c.id, name: c.first_name }); setScreen('award'); }}>
                                {`${c.first_name} · ${new Date(c.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                            </BigButton>
                        ))}
                        <BigButton theme={theme} {...paint('desk_redeem_btn')} tone="quiet"
                                   testid="loyalty-desk-redeem-open"
                                   onClick={() => { setForm({}); setDone(''); setScreen('redeem'); }}>
                            {text('desk_redeem_btn', t('loyalty.desk.redeemCard', 'Redeem a gift card'))}
                        </BigButton>
                    </>
                )}

                {screen === 'award' && (
                    <>
                        <BackBar onBack={() => setScreen('staff')} label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-desk-award-title">{form.name}</Title>
                        <Field theme={theme} label={text('desk_amount_label',
                            t('loyalty.desk.amountLabel',
                              'Total bill amount today (before taxes)'))}
                               testid="loyalty-desk-amount"
                               {...moneyFieldProps(form.amount || '',
                                   (next) => setForm({ ...form, amount: next }), lang)} />
                        {errorLine}
                        <BigButton theme={theme} {...paint('desk_accept_btn')}
                                   disabled={busy || !Number(plain(form.amount))}
                                   testid="loyalty-desk-accept" onClick={award}>
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                                : text('desk_accept_btn', t('loyalty.desk.accept', 'Accept'))}
                        </BigButton>
                    </>
                )}

                {screen === 'redeem' && (
                    <>
                        <BackBar onBack={() => setScreen('staff')} label={t('common.back', 'Back')} />
                        <Title theme={theme}>
                            {t('loyalty.desk.redeemCard', 'Redeem a gift card')}
                        </Title>
                        {scanning ? (
                            <QrScan onClose={() => setScanning(false)}
                                    onFound={(code) => {
                                        setScanning(false);
                                        setForm({ ...form, code: String(code).split('/').pop() });
                                    }} />
                        ) : canScan() && (
                            <BigButton theme={theme} tone="quiet"
                                       testid="loyalty-desk-scan-open"
                                       onClick={() => setScanning(true)}>
                                {t('loyalty.desk.scan', 'Scan the code')}
                            </BigButton>
                        )}
                        <Field theme={theme} label={t('loyalty.desk.codeLabel', 'Gift card code')}
                               value={form.code || ''} testid="loyalty-desk-code"
                               onChange={(e) => setForm({ ...form, code: e.target.value, kind: null })}
                               onBlur={() => lookUp(form.code)} />
                        {form.kind === 'certificate' && (
                            <>
                                <Field theme={theme} label={text('desk_amount_label',
                                    t('loyalty.desk.amountLabel',
                                      'Total bill amount today (before taxes)'))}
                                       testid="loyalty-desk-redeem-bill"
                                       {...moneyFieldProps(form.bill || '',
                                           (next) => setForm({ ...form, bill: next }), lang)} />
                                <p className="text-xs opacity-70" style={{ color: theme.text || '#fff' }}>
                                    {t('loyalty.desk.whoLabel', 'Who is it for? She must have signed in.')}
                                </p>
                                {checkins.map((c) => (
                                    <BigButton key={c.id} theme={theme}
                                               tone={form.checkin_id === c.id ? 'primary' : 'quiet'}
                                               testid={`loyalty-desk-redeem-who-${c.id}`}
                                               onClick={() => setForm({ ...form, checkin_id: c.id })}>
                                        {c.first_name}
                                    </BigButton>
                                ))}
                            </>
                        )}
                        {errorLine}
                        <BigButton theme={theme} disabled={busy || !form.code}
                                   testid="loyalty-desk-redeem-go" onClick={redeem}>
                            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
                                : t('loyalty.desk.redeemCard', 'Redeem a gift card')}
                        </BigButton>
                    </>
                )}

                {screen === 'manager' && staff && (
                    <>
                        <BackBar onBack={() => { setScreen('pending'); setError(''); }}
                                 label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-desk-manager-title">
                            {text('desk_manager_title', t('loyalty.desk.manager', 'Management'))}
                        </Title>
                        {errorLine}
                        {desk?.may_approve && (
                            <BigButton theme={theme} {...paint('desk_pending_btn')}
                                       testid="loyalty-desk-pending-open"
                                       light={lightsOn
                                           ? <Light count={(desk?.pending || []).length}
                                                    testid="loyalty-desk-light-pending" />
                                           : null}
                                       onClick={() => setScreen('pending')}>
                                {text('desk_pending_btn',
                                    t('loyalty.desk.pendingTitle', 'Waiting for approval'))}
                            </BigButton>
                        )}
                        {desk?.may_see_awards && (
                            <BigButton theme={theme} {...paint('desk_awards_btn')} tone="quiet"
                                       testid="loyalty-desk-awards-open"
                                       onClick={() => setScreen('awards')}>
                                {text('desk_awards_btn',
                                    t('loyalty.desk.awardsTitle', "Today's awards"))}
                            </BigButton>
                        )}
                        <BigButton theme={theme} tone="quiet" testid="loyalty-desk-manager-staff"
                                   light={lightsOn
                                       ? <Light count={checkins.length}
                                                testid="loyalty-desk-light-checkins" />
                                       : null}
                                   onClick={() => setScreen('staff')}>
                            {text('desk_checkins_title',
                                t('loyalty.desk.checkinsTitle', "Today's check-ins"))}
                        </BigButton>
                        {desk?.may_set_expiry && (
                            <div className="space-y-2" data-testid="loyalty-desk-expiry-box">
                                <Field theme={theme} label={t('loyalty.desk.expiryTitle',
                                    'A check-in stays open for (hours)')}
                                       inputMode="numeric" testid="loyalty-desk-expiry"
                                       value={form.hours ?? String(desk.checkin_hours)}
                                       onChange={(e) => setForm({ ...form, hours: e.target.value })} />
                                <BigButton theme={theme} tone="quiet" disabled={busy}
                                           testid="loyalty-desk-expiry-save"
                                           onClick={async () => {
                                               const out = await post('/manager/expiry',
                                                   { hours: Number(form.hours || desk.checkin_hours) },
                                                   { 'X-Loyalty-Staff': staff.token });
                                               if (out) { setDone(t('loyalty.desk.saved', 'Saved'));
                                                   await loadDesk(out.session || staff.token); }
                                           }}>
                                    {t('common.save', 'Save')}
                                </BigButton>
                            </div>
                        )}
                    </>
                )}

                {screen === 'pending' && staff && (
                    <>
                        <BackBar onBack={() => { setStaff(null); setScreen('home'); }}
                                 label={t('loyalty.desk.signOut', 'Sign out')} />
                        <Title theme={theme} testid="loyalty-desk-pending-title">
                            {text('desk_pending_btn',
                                t('loyalty.desk.pendingTitle', 'Waiting for approval'))}
                        </Title>
                        {done && (
                            <p className="text-center text-sm" data-testid="loyalty-desk-decided-note">
                                {done}
                            </p>
                        )}
                        {errorLine}
                        {(desk?.pending || []).length === 0 && (
                            <p className="text-center text-sm opacity-80"
                               data-testid="loyalty-desk-no-pending">
                                {t('loyalty.desk.noPending', 'Nothing is waiting.')}
                            </p>
                        )}
                        {(desk?.pending || []).map((row) => (
                            <div key={row.id} className="rounded-2xl bg-white/5 p-3 text-sm"
                                 style={{ color: theme.text || '#fff' }}
                                 data-testid={`loyalty-desk-pending-${row.id}`}>
                                <p className="font-semibold">{row.member_name}</p>
                                <p className="text-xs opacity-70">
                                    {`${money(row.bill_amount, lang)} · `}
                                    {`${Number(row.points).toLocaleString()} `}
                                    {t('loyalty.points', 'points')}
                                    {` · ${row.staff_name} · ${when(row.created_at)}`}
                                </p>
                                <BigButton theme={theme} {...paint('desk_approve_btn')} disabled={busy}
                                           testid={`loyalty-desk-approve-${row.id}`}
                                           onClick={() => decide('approve', row, false)}>
                                    {text('desk_approve_btn', t('loyalty.desk.approve', 'Approve'))}
                                </BigButton>
                                <button type="button" className="mt-2 w-full text-center text-xs underline opacity-70"
                                        data-testid={`loyalty-desk-more-${row.id}`}
                                        onClick={() => { setForm({ award: row,
                                            amount: amountOnly(row.bill_amount) }); setScreen('decide'); }}>
                                    {t('loyalty.desk.changeOrRefuse', 'Change or refuse')}
                                </button>
                            </div>
                        ))}
                        {(desk?.pending || []).length > 1 && (
                            <BigButton theme={theme} tone="quiet" disabled={busy}
                                       testid="loyalty-desk-approve-all"
                                       onClick={() => setScreen('approve_all')}>
                                {t('loyalty.desk.approveAll', 'Approve all')}
                            </BigButton>
                        )}
                        <button type="button" className="w-full text-center text-xs underline opacity-70"
                                data-testid="loyalty-desk-manager-more"
                                onClick={() => setScreen('manager')}>
                            {t('loyalty.desk.managerMore', 'Other things')}
                        </button>
                    </>
                )}

                {screen === 'approve_all' && staff && (
                    <>
                        <BackBar onBack={() => setScreen('pending')} label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-desk-approve-all-title">
                            {t('loyalty.desk.approveAllAsk', 'Approve all {{count}} lines?',
                                { count: (desk?.pending || []).length })}
                        </Title>
                        {errorLine}
                        <BigButton theme={theme} {...paint('desk_approve_btn')} disabled={busy}
                                   testid="loyalty-desk-approve-all-go"
                                   onClick={async () => {
                                       const rows = [...(desk?.pending || [])];
                                       for (const row of rows) {
                                           // eslint-disable-next-line no-await-in-loop
                                           await post('/manager/decide',
                                               { award_id: row.id, decision: 'approve' },
                                               { 'X-Loyalty-Staff': staff.token });
                                       }
                                       setDone(t('loyalty.desk.saved', 'Saved'));
                                       await loadDesk(staff.token);
                                       setScreen('pending');
                                   }}>
                            {t('loyalty.desk.approve', 'Approve')}
                        </BigButton>
                    </>
                )}

                {screen === 'decide' && staff && form.award && (
                    <>
                        <BackBar onBack={() => { setScreen('pending'); setError(''); }}
                                 label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-desk-decide-title">
                            {form.award.member_name}
                        </Title>
                        <p className="text-center text-sm opacity-80" data-testid="loyalty-desk-decide-line">
                            {`${form.award.staff_name} · ${money(form.award.bill_amount, lang)} · `}
                            {`${Number(form.award.points).toLocaleString()} `}
                            {t('loyalty.points', 'points')}
                        </p>
                        {desk?.may_change && (
                            <Field theme={theme} label={t('loyalty.desk.newAmount', 'Corrected total bill amount today (before taxes)')}
                                   testid="loyalty-desk-decide-amount"
                                   {...moneyFieldProps(form.amount || '',
                                       (next) => setForm({ ...form, amount: next }), lang)} />
                        )}
                        <Field label={t('loyalty.desk.reasonLabel', 'Reason')}
                               testid="loyalty-desk-decide-reason" value={form.reason || ''}
                               onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                        {errorLine}
                        <BigButton theme={theme} {...paint('desk_approve_btn')} disabled={busy}
                                   testid="loyalty-desk-decide-approve"
                                   onClick={() => decide('approve')}>
                            {text('desk_approve_btn', t('loyalty.desk.approve', 'Approve'))}
                        </BigButton>
                        <BigButton theme={theme} {...paint('desk_reject_btn')} tone="quiet"
                                   disabled={busy} testid="loyalty-desk-decide-reject"
                                   onClick={() => decide('reject')}>
                            {text('desk_reject_btn', t('loyalty.desk.reject', 'Not added'))}
                        </BigButton>
                    </>
                )}

                {screen === 'awards' && staff && (
                    <>
                        <BackBar onBack={() => setScreen('manager')} label={t('common.back', 'Back')} />
                        <Title theme={theme} testid="loyalty-desk-awards-title">
                            {text('desk_awards_btn', t('loyalty.desk.awardsTitle', 'Points added today'))}
                        </Title>
                        {errorLine}
                        {(desk?.lines || []).length === 0 && (
                            <p className="text-center text-sm opacity-80"
                               data-testid="loyalty-desk-no-awards">
                                {t('loyalty.desk.noAwards', 'No points added today.')}
                            </p>
                        )}
                        {(desk?.lines || []).map((line) => (
                            <div key={line.id} className="rounded-2xl bg-white/5 p-3 text-sm"
                                 style={{ color: theme.text || '#fff' }}
                                 data-testid={`loyalty-desk-line-${line.id}`}>
                                <p className="font-semibold">{line.member_name}</p>
                                <p className="text-xs opacity-70">
                                    {`${line.staff_name || '—'} · ${money(line.bill_amount, lang)} · `}
                                    {`${Number(line.points).toLocaleString()} `}
                                    {t('loyalty.points', 'points')}
                                    {` · ${t(`loyalty.desk.status_${line.status}`, line.status)}`}
                                </p>
                                {line.status === 'approved' && (
                                    <>
                                        <Field theme={theme} label={t('loyalty.desk.newAmount', 'Corrected total bill amount today (before taxes)')}
                                               testid={`loyalty-desk-line-amount-${line.id}`}
                                               {...moneyFieldProps(form[`a_${line.id}`] || '',
                                                   (next) => setForm({ ...form, [`a_${line.id}`]: next }), lang)} />
                                        <Field label={t('loyalty.desk.reasonLabel', 'Reason')}
                                               testid={`loyalty-desk-line-reason-${line.id}`}
                                               value={form[`r_${line.id}`] || ''}
                                               onChange={(e) => setForm({ ...form, [`r_${line.id}`]: e.target.value })} />
                                        <BigButton theme={theme} tone="quiet" disabled={busy || !form[`r_${line.id}`]}
                                                   testid={`loyalty-desk-line-correct-${line.id}`}
                                                   onClick={async () => {
                                                       const out = await post('/manager/correct', {
                                                           award_id: line.id,
                                                           amount: Number(plain(form[`a_${line.id}`])),
                                                           reason: form[`r_${line.id}`] || '',
                                                       }, { 'X-Loyalty-Staff': staff.token });
                                                       if (out) { setForm({});
                                                           await loadDesk(out.session || staff.token); }
                                                   }}>
                                            {t('loyalty.desk.changeAmount', 'Change amount')}
                                        </BigButton>
                                        <BigButton theme={theme} tone="quiet" disabled={busy || !form[`r_${line.id}`]}
                                                   testid={`loyalty-desk-line-takeback-${line.id}`}
                                                   onClick={async () => {
                                                       const out = await post('/manager/take-back', {
                                                           award_id: line.id,
                                                           reason: form[`r_${line.id}`] || '',
                                                       }, { 'X-Loyalty-Staff': staff.token });
                                                       if (out) { setForm({});
                                                           await loadDesk(out.session || staff.token); }
                                                   }}>
                                            {t('loyalty.desk.takeBack', 'Take back')}
                                        </BigButton>
                                    </>
                                )}
                                {line.status === 'rejected' && (
                                    <BigButton theme={theme} tone="quiet" disabled={busy}
                                               testid={`loyalty-desk-reconsider-${line.id}`}
                                               onClick={async () => {
                                                   const out = await post('/manager/decide', {
                                                       award_id: line.id, decision: 'reconsider',
                                                       reason: t('loyalty.desk.reconsider', 'Reconsider'),
                                                   }, { 'X-Loyalty-Staff': staff.token });
                                                   if (out) await loadDesk(out.session || staff.token);
                                               }}>
                                        {t('loyalty.desk.reconsider', 'Reconsider')}
                                    </BigButton>
                                )}
                            </div>
                        ))}
                        {(desk?.redeemed || []).map((card) => (
                            <div key={card.code} className="rounded-2xl bg-white/5 p-3 text-sm"
                                 style={{ color: theme.text || '#fff' }}
                                 data-testid={`loyalty-desk-used-${card.code}`}>
                                <p>{t('loyalty.desk.usedLine', 'Used — {{value}}',
                                    { value: money(card.value, lang) })}</p>
                                <p className="text-xs opacity-60">{card.code}</p>
                                <BigButton theme={theme} tone="quiet" disabled={busy}
                                           testid={`loyalty-desk-undo-used-${card.code}`}
                                           onClick={async () => {
                                               const out = await post('/manager/undo-redeem',
                                                   { code: card.code },
                                                   { 'X-Loyalty-Staff': staff.token });
                                               if (out) await loadDesk(out.session || staff.token);
                                           }}>
                                    {t('common.undo', 'Undo')}
                                </BigButton>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </Screen>
    );
}
